import React, { useState, useEffect } from 'react';
import {
  getProxmoxHosts,
  getFirewallStatus,
  getFirewallRules,
  createFirewallRule,
  updateFirewallRule,
  deleteFirewallRule,
  toggleFirewallRule,
  ProxmoxHost,
  FirewallRule,
  FirewallStatus,
  CreateFirewallRuleData
} from '../../services/proxmoxHostFirewallService';

const ProxmoxHostFirewallManagement: React.FC = () => {
  const [hosts, setHosts] = useState<ProxmoxHost[]>([]);
  const [selectedHostId, setSelectedHostId] = useState<number | null>(null);
  const [status, setStatus] = useState<FirewallStatus | null>(null);
  const [rules, setRules] = useState<FirewallRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRules, setLoadingRules] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingRule, setEditingRule] = useState<FirewallRule | null>(null);
  const [saving, setSaving] = useState(false);

  const [ruleForm, setRuleForm] = useState<CreateFirewallRuleData>({
    direction: 'IN',
    action: 'ACCEPT',
    protocol: 'tcp',
    port: '',
    source_ip: '',
    dest_ip: '',
    comment: '',
    is_active: true,
    priority: 100
  });

  // Common ports for quick add
  const commonPorts = [
    { name: 'SSH', port: '22', protocol: 'tcp' as const },
    { name: 'HTTP', port: '80', protocol: 'tcp' as const },
    { name: 'HTTPS', port: '443', protocol: 'tcp' as const },
    { name: 'Proxmox Web', port: '8006', protocol: 'tcp' as const },
    { name: 'Plesk Web', port: '8443', protocol: 'tcp' as const },
    { name: 'Plesk Alt', port: '8880', protocol: 'tcp' as const },
    { name: 'SMTP', port: '25', protocol: 'tcp' as const },
    { name: 'SMTPS', port: '465', protocol: 'tcp' as const },
    { name: 'Submission', port: '587', protocol: 'tcp' as const },
    { name: 'POP3', port: '110', protocol: 'tcp' as const },
    { name: 'POP3S', port: '995', protocol: 'tcp' as const },
    { name: 'IMAP', port: '143', protocol: 'tcp' as const },
    { name: 'IMAPS', port: '993', protocol: 'tcp' as const },
    { name: 'DNS', port: '53', protocol: 'tcp' as const },
    { name: 'DNS UDP', port: '53', protocol: 'udp' as const },
    { name: 'FTP', port: '21', protocol: 'tcp' as const },
    { name: 'MySQL', port: '3306', protocol: 'tcp' as const },
    { name: 'PostgreSQL', port: '5432', protocol: 'tcp' as const },
  ];

  useEffect(() => {
    fetchHosts();
  }, []);

  useEffect(() => {
    if (selectedHostId) {
      fetchStatusAndRules();
    }
  }, [selectedHostId]);

  const fetchHosts = async () => {
    try {
      setLoading(true);
      const data = await getProxmoxHosts();
      setHosts(data);
      if (data.length > 0 && !selectedHostId) {
        setSelectedHostId(data[0].id);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusAndRules = async () => {
    if (!selectedHostId) return;

    try {
      setLoadingRules(true);
      setError('');
      const [statusData, rulesData] = await Promise.all([
        getFirewallStatus(selectedHostId),
        getFirewallRules(selectedHostId)
      ]);
      setStatus(statusData);
      setRules(rulesData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingRules(false);
    }
  };

  const handleAddRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedHostId) return;

    try {
      setSaving(true);
      setError('');
      await createFirewallRule(selectedHostId, ruleForm);
      setSuccess('Rule created and applied to server');
      setShowAddRule(false);
      resetForm();
      fetchStatusAndRules();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRule) return;

    try {
      setSaving(true);
      setError('');
      await updateFirewallRule(editingRule.id, ruleForm);
      setSuccess('Rule updated and applied to server');
      setEditingRule(null);
      resetForm();
      fetchStatusAndRules();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this rule? This will be applied to the server immediately.')) return;

    try {
      setSaving(true);
      setError('');
      await deleteFirewallRule(ruleId);
      setSuccess('Rule deleted and removed from server');
      fetchStatusAndRules();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (ruleId: number) => {
    try {
      setSaving(true);
      setError('');
      await toggleFirewallRule(ruleId);
      setSuccess('Rule status updated on server');
      fetchStatusAndRules();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setRuleForm({
      direction: 'IN',
      action: 'ACCEPT',
      protocol: 'tcp',
      port: '',
      source_ip: '',
      dest_ip: '',
      comment: '',
      is_active: true,
      priority: 100
    });
  };

  const startEdit = (rule: FirewallRule) => {
    setEditingRule(rule);
    setRuleForm({
      direction: rule.direction,
      action: rule.action,
      protocol: rule.protocol,
      port: rule.port,
      source_ip: rule.source_ip || '',
      dest_ip: rule.dest_ip || '',
      comment: rule.comment || '',
      is_active: rule.is_active,
      priority: rule.priority
    });
    setShowAddRule(false);
  };

  const quickAddPort = (port: typeof commonPorts[0]) => {
    setRuleForm({
      ...ruleForm,
      port: port.port,
      protocol: port.protocol,
      comment: port.name
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
      </div>
    );
  }

  const selectedHost = hosts.find(h => h.id === selectedHostId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Proxmox Host Firewall Management</h2>

        {/* Host Selector */}
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Select Host:</label>
          <select
            value={selectedHostId || ''}
            onChange={(e) => setSelectedHostId(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          >
            {hosts.map(host => (
              <option key={host.id} value={host.id}>
                {host.name} ({host.hostname})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError('')} className="text-red-500 text-sm underline mt-1">Dismiss</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
          <button onClick={() => setSuccess('')} className="text-green-500 text-sm underline mt-1">Dismiss</button>
        </div>
      )}

      {selectedHostId && (
        <>
          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`rounded-lg p-4 border ${status?.enabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <h3 className="text-sm font-medium text-gray-600">Firewall Status</h3>
              <p className={`text-2xl font-bold ${status?.enabled ? 'text-green-600' : 'text-red-600'}`}>
                {status?.enabled ? 'ENABLED' : 'DISABLED'}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-600">Active Rules</h3>
              <p className="text-2xl font-bold text-blue-600">{rules.filter(r => r.is_active).length}</p>
            </div>

            <div className={`rounded-lg p-4 border ${status?.fail2ban?.enabled ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
              <h3 className="text-sm font-medium text-gray-600">Fail2ban</h3>
              <p className={`text-2xl font-bold ${status?.fail2ban?.enabled ? 'text-green-600' : 'text-yellow-600'}`}>
                {status?.fail2ban?.enabled ? 'ACTIVE' : 'INACTIVE'}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => { setShowAddRule(true); setEditingRule(null); resetForm(); }}
              disabled={saving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center disabled:opacity-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Rule
            </button>

            <button
              onClick={fetchStatusAndRules}
              disabled={loadingRules || saving}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium flex items-center disabled:opacity-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>

            {saving && (
              <div className="flex items-center text-brand-orange">
                <span className="animate-spin mr-2">&#9696;</span>
                Applying changes...
              </div>
            )}
          </div>

          {/* Add/Edit Rule Form */}
          {(showAddRule || editingRule) && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingRule ? 'Edit Firewall Rule' : 'Add New Firewall Rule'}
              </h3>

              {/* Quick Add Buttons */}
              {!editingRule && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Quick add common ports:</p>
                  <div className="flex flex-wrap gap-2">
                    {commonPorts.map((port, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => quickAddPort(port)}
                        className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100"
                      >
                        {port.name} ({port.port})
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={editingRule ? handleUpdateRule : handleAddRule} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
                  <select
                    value={ruleForm.direction}
                    onChange={(e) => setRuleForm({ ...ruleForm, direction: e.target.value as 'IN' | 'OUT' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange"
                  >
                    <option value="IN">IN (Incoming)</option>
                    <option value="OUT">OUT (Outgoing)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                  <select
                    value={ruleForm.action}
                    onChange={(e) => setRuleForm({ ...ruleForm, action: e.target.value as 'ACCEPT' | 'DROP' | 'REJECT' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange"
                  >
                    <option value="ACCEPT">ACCEPT</option>
                    <option value="DROP">DROP</option>
                    <option value="REJECT">REJECT</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Protocol</label>
                  <select
                    value={ruleForm.protocol}
                    onChange={(e) => setRuleForm({ ...ruleForm, protocol: e.target.value as 'tcp' | 'udp' | 'icmp' | 'any' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange"
                  >
                    <option value="tcp">TCP</option>
                    <option value="udp">UDP</option>
                    <option value="icmp">ICMP</option>
                    <option value="any">Any</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Port(s) *</label>
                  <input
                    type="text"
                    value={ruleForm.port}
                    onChange={(e) => setRuleForm({ ...ruleForm, port: e.target.value })}
                    placeholder="e.g., 80, 443, 8000:8100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source IP (optional)</label>
                  <input
                    type="text"
                    value={ruleForm.source_ip}
                    onChange={(e) => setRuleForm({ ...ruleForm, source_ip: e.target.value })}
                    placeholder="e.g., 192.168.1.0/24"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Destination IP (optional)</label>
                  <input
                    type="text"
                    value={ruleForm.dest_ip}
                    onChange={(e) => setRuleForm({ ...ruleForm, dest_ip: e.target.value })}
                    placeholder="e.g., 10.0.0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
                  <input
                    type="text"
                    value={ruleForm.comment}
                    onChange={(e) => setRuleForm({ ...ruleForm, comment: e.target.value })}
                    placeholder="e.g., Allow HTTP traffic"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number"
                    value={ruleForm.priority}
                    onChange={(e) => setRuleForm({ ...ruleForm, priority: parseInt(e.target.value) || 100 })}
                    min="1"
                    max="999"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={ruleForm.is_active}
                      onChange={(e) => setRuleForm({ ...ruleForm, is_active: e.target.checked })}
                      className="mr-2 h-4 w-4"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>

                <div className="md:col-span-3 flex justify-end space-x-3 mt-4">
                  <button
                    type="button"
                    onClick={() => { setShowAddRule(false); setEditingRule(null); resetForm(); }}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-brand-orange hover:bg-orange-600 text-white rounded-lg"
                  >
                    {editingRule ? 'Update Rule' : 'Create Rule'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Rules Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Firewall Rules for {selectedHost?.name}</h3>
            </div>

            {loadingRules ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-orange"></div>
              </div>
            ) : rules.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No firewall rules configured. Click "Add Rule" to create a new rule.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direction</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Protocol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Port</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rules.sort((a, b) => a.priority - b.priority).map((rule) => (
                      <tr key={rule.id} className={!rule.is_active ? 'bg-gray-50 opacity-60' : ''}>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleRule(rule.id)}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              rule.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {rule.is_active ? 'Active' : 'Disabled'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            rule.direction === 'IN' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {rule.direction}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            rule.action === 'ACCEPT' ? 'bg-green-100 text-green-800' :
                            rule.action === 'DROP' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {rule.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{rule.protocol.toUpperCase()}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">{rule.port}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{rule.source_ip || 'Any'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{rule.comment || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{rule.priority}</td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => startEdit(rule)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteRule(rule.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {hosts.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <p className="text-yellow-700">No Proxmox hosts configured. Please add a host in the "Proxmox Hosts" tab first.</p>
        </div>
      )}
    </div>
  );
};

export default ProxmoxHostFirewallManagement;
